/**
 * Known error codes from backend API
 *
 * Organized by domain for easy maintenance and type safety.
 * Use these constants instead of string literals to avoid typos.
 *
 * ✅ Domain Layer - Pure constants, no dependencies
 *
 * @example
 * if (error.code === ERROR_CODES.ROLES.PERMISSIONS_NOT_FOUND) {
 *   // Handle specific error
 * }
 */
export const ERROR_CODES = {
  /**
   * Role-related error codes
   */
  ROLES: {
    /** Backend returns when permissions in request don't exist */
    PERMISSIONS_NOT_FOUND: 'roles.PermissionsNotFound',
    /** Backend returns when trying to create a role with a name that already exists */
    ROLE_ALREADY_EXISTS: 'roles.RoleAlreadyExists',

    // Add more as you discover them from backend
    // ROLE_NOT_FOUND: 'roles.RoleNotFound',
    // ROLE_IN_USE: 'roles.RoleInUse',
  },

  /**
   * Employee-related error codes
   */
  EMPLOYEES: {
    /** Email is already registered in the system */
    EMAIL_ALREADY_IN_USE: 'parrot.EmailAlreadyInUse',
    /** PIN is already in use by another employee */
    PIN_ALREADY_IN_USE: 'parrot.PinAlreadyInUse',
    /** Cannot delete a Super Admin user */
    CANNOT_DELETE_SUPER_ADMIN: 'users.CannotDeleteSuperAdmin',
  },

  /**
   * Common/generic error codes
   */
  COMMON: {
    /** Generic server error for all 5xx responses */
    SERVER_ERROR: 'common.ServerError',
    /** Network connectivity issues */
    NETWORK_ERROR: 'common.NetworkError',
    /** Catch-all for unknown errors */
    UNEXPECTED_ERROR: 'common.UnexpectedError',
  },
} as const;

/**
 * Type for all possible error codes
 * Automatically derived from ERROR_CODES constant
 */
export type ErrorCode = typeof ERROR_CODES[keyof typeof ERROR_CODES][keyof typeof ERROR_CODES[keyof typeof ERROR_CODES]];

/**
 * Helper type guard to check if a string is a known error code
 *
 * @param code - String to check
 * @returns True if code matches a known ERROR_CODE
 *
 * @example
 * if (isKnownErrorCode(error.code)) {
 *   // TypeScript knows it's a valid ErrorCode
 * }
 */
export const isKnownErrorCode = (code: string): code is ErrorCode => {
  const allCodes = Object.values(ERROR_CODES).flatMap((domain) => Object.values(domain));
  return allCodes.includes(code as ErrorCode);
};

/**
 * Mapping of error codes to their interpolation patterns
 *
 * Defines which errors need dynamic data extraction and how to extract it
 */
export const ERROR_INTERPOLATION_PATTERNS: Partial<Record<ErrorCode, RegExp>> = {
  [ERROR_CODES.ROLES.PERMISSIONS_NOT_FOUND]: /do not exist: (.+)$/,

  // Add more patterns as needed
  // [ERROR_CODES.ROLES.ROLE_IN_USE]: /assigned to (\d+) user/,
};

/**
 * Gets interpolation field name for an error code
 *
 * @param errorCode - The error code constant
 * @returns The field name for interpolation (e.g., 'permissions', 'count')
 */
export const getInterpolationField = (errorCode: ErrorCode): string | undefined => {
  switch (errorCode) {
    case ERROR_CODES.ROLES.PERMISSIONS_NOT_FOUND:
      return 'permissions';
    // Add more cases as needed
    default:
      return undefined;
  }
};
